import React from 'react'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom'
import styles from './toast.module.scss'

// Global popup state management
let upgradePopupCallback: ((isOpen: boolean, title?: string, subtitle?: string) => void) | null = null;

export const setUpgradePopupCallback = (callback: (isOpen: boolean, title?: string, subtitle?: string) => void) => {
    upgradePopupCallback = callback;
};

export const showUpgradePopup = (title?: string, subtitle?: string) => {
    if (upgradePopupCallback) {
        upgradePopupCallback(true, title, subtitle);
    }
};

export const hideUpgradePopup = () => {
    if (upgradePopupCallback) {
        upgradePopupCallback(false);
    }
};

export const showUpgradePopupInsteadOfToast = (
    isTrial: boolean, 
    isTooManyWords: boolean = false, 
    t: (key: string) => string,
    onUpgrade?: () => void
) => {
    if (isTrial) {
        const title = t('upgradePopup.trial.title');
        const subtitle = t('upgradePopup.trial.subtitle');
        showUpgradePopup(title, subtitle);
    } else if (isTooManyWords) {
        const title = t('upgradePopup.limit.title');
        const subtitle = t('upgradePopup.limit.subtitle');
        showUpgradePopup(title, subtitle);
    } else {
        const title = t('upgradePopup.general.title');
        const subtitle = t('upgradePopup.general.subtitle');
        showUpgradePopup(title, subtitle);
    }
};

export const showUpgradePopupFromButton = (context: 'detector' | 'general' | 'feature', t?: (key: string) => string) => {
    if (!t) return;
    
    let title: string;
    let subtitle: string;
    
    switch (context) {
        case 'detector':
            title = t('upgradePopup.detector.title');
            subtitle = t('upgradePopup.detector.subtitle');
            break;
        case 'feature':
            title = t('upgradePopup.feature.title');
            subtitle = t('upgradePopup.feature.subtitle');
            break;
        default:
            title = t('upgradePopup.general.title');
            subtitle = t('upgradePopup.general.subtitle');
    }
    
    showUpgradePopup(title, subtitle);
};

export const makeToast = (
    type: 'success' | 'error' | 'warn',
    message: string
) => {
    if (type === 'success') return toast.success(message)
    if (type === 'warn') return toast.warn(message)
    return toast.error(message)
}

let overlayElement: HTMLDivElement | null = null;

const createOverlay = (closeToast: () => void) => {
  if (!overlayElement) {
    overlayElement = document.createElement('div');
    overlayElement.className = styles.overlay;
    overlayElement.addEventListener('click', () => {
      closeToast();
      hideOverlay();
    });
    document.body.appendChild(overlayElement);
  }
};

const showOverlay = () => {
  if (overlayElement) {
    overlayElement.classList.add(styles.visible);
  }
};

const hideOverlay = () => {
  if (overlayElement) {
    overlayElement.classList.remove(styles.visible);
  }
};

const SUBSCRIPTION_TOAST_ID = 'subscription-toast'
const ENHANCED_MODEL_TOAST_ID = 'enhanced-model-toast'

export const makeSubscriptionToast = (
    isTrial: boolean, 
    isTooManyWords: boolean = false, 
    t: (key: string) => string,
    onUpgrade?: () => void
) => {
    showUpgradePopupInsteadOfToast(isTrial, isTooManyWords, t, onUpgrade);
}

export const makeEnhancedModelToast = (
    t: (key: string) => string, 
    isTrialOrNotRegistered: boolean,
    onUpgrade?: () => void
) => {
    // This function is now deprecated - use StealthModelPopup instead
    // But we'll keep it for backward compatibility
    if (!toast.isActive(ENHANCED_MODEL_TOAST_ID)) {
        toast.info(
            ({ closeToast }: { closeToast: () => void }) => {
                createOverlay(closeToast);
                showOverlay();
                
                const handleClose = () => {
                    closeToast();
                    hideOverlay();
                };

                const handleUpgradeClick = () => {
                    if (onUpgrade) {
                        onUpgrade();
                    }
                    handleClose();
                };

                return (
                    <div className={styles.subscriptionToast}>
                        <h3>{t('enhancedModelToast.title')}</h3>
                        <div className={styles.toastText}>
                            {t('enhancedModelToast.desc1')}
                        </div>
                        <div className={styles.toastText}>
                            {t('enhancedModelToast.desc2')}
                        </div>
                        {isTrialOrNotRegistered && (
                            <>
                                <button onClick={handleUpgradeClick}>
                                    {t('enhancedModelToast.button')}
                                </button>
                                <div className={styles.guaranteeText}>
                                    {t('subscriptionToast.moneyBack')}
                                </div>
                                <div className={styles.chooseAnotherPlan}>
                                    <a href="/pricing">{t('subscriptionToast.chooseAnotherPlan')}</a>
                                </div>
                            </>
                        )}
                    </div>
                );
            },
            {
                toastId: ENHANCED_MODEL_TOAST_ID,
                position: "top-center",
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                closeButton: true,
                className: styles.subscriptionToastContainer,
                icon: false,
                onClose: () => {
                    hideOverlay();
                }
            }
        )
    }
}

// New function to trigger StealthModelPopup
export const triggerStealthModelPopup = (callback: () => void) => {
    callback();
}